import { Modal, Form, Input, Button, Switch } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface DepartmentFormValues {
    name: string;
    description?: string;
    is_active?: boolean;
}

interface DepartmentModalProps {
    open: boolean;
    mode: "add" | "edit";
    initialValues?: DepartmentFormValues;
    onCancel: () => void;
    onSubmit: (values: DepartmentFormValues) => void;
    loading?: boolean;
}

export default function AddModal({
    open,
    mode,
    initialValues,
    onCancel,
    onSubmit,
    loading,
}: DepartmentModalProps) {
    const [form] = Form.useForm();
    const { t } = useTranslation();

    useEffect(() => {
        if (open) {
            form.setFieldsValue(
                initialValues || {
                    name: "",
                    description: "",
                    is_active: true,
                }
            );
        }
    }, [open, initialValues, form]);

    const handleFinish = (values: DepartmentFormValues) => {
        onSubmit(values);
    };

    return (
        <Modal
            title={mode === "add" ? t('departments.modal_add_title') : t('departments.modal_edit_title')}
            open={open}
            onCancel={() => {
                form.resetFields();
                onCancel();
            }}
            footer={null}
            centered
            destroyOnClose
            className="dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                dark:[&_.ant-modal-close]:text-white
                dark:[&_.ant-modal-title]:text-white"
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Form.Item
                    label={<span className="t-text-white">{t('departments.label_name')}</span>}
                    name="name"
                    rules={[{ required: true, message: t('departments.name_required') }]}
                >
                    <Input
                        placeholder={t('departments.placeholder_name')}
                        className="dark:bg-[#141414] t-text-white dark:border-gray-600"
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">{t('departments.label_description')}</span>}
                    name="description"
                >
                    <Input.TextArea
                        rows={4}
                        placeholder={t('departments.placeholder_description')}
                        className="dark:bg-[#141414] t-text-white dark:border-gray-600"
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">{t('departments.active_status')}</span>}
                    name="is_active"
                    valuePropName="checked"
                >
                    <Switch
                        className="[&.ant-switch-checked]:!bg-[#3e91ab]"
                    />
                </Form.Item>

                <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
                    <Button onClick={onCancel}>{t('departments.cancel')}</Button>
                    <Button
                        htmlType="submit"
                        type="primary"
                        loading={loading}
                        className="!bg-[#3e91ab] hover:!bg-[#357f96] border-none"
                    >
                        OK
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}
